import { useEffect, useRef, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { uploadExpenseFile, scanExpenseFile, recheckScan, applyScan } from "../../api/expenses";
import { shrinkImage } from "../../utils/files";
import { INPUT } from "./ExpenseModal";

// Snap a receipt or upload a statement; the AI reads it and suggests rows.
// You tick what to add. Nothing is saved until "Add".

const TYPE_LABEL = { expense: "Expense", money_in: "Money in", skip: "Skipped" };
const LAST_ACCOUNT = "bb:lastScanAccount";

function lastAccount(accounts) {
  try {
    const id = Number(localStorage.getItem(LAST_ACCOUNT));
    return accounts.some((a) => a.id === id && !a.archived) ? id : "";
  } catch {
    return "";
  }
}

export default function ScanModal({ data, onClose }) {
  const [stage, setStage] = useState("pick"); // pick | reading | review
  const [scan, setScan] = useState(null);
  const [rows, setRows] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    // Open the picker straight away: one tap from "Scan" to the camera roll.
    const t = setTimeout(() => fileRef.current?.click(), 50);
    return () => clearTimeout(t);
  }, []);

  async function pick(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError("");
    setStage("reading");
    try {
      const up = await uploadExpenseFile(await shrinkImage(f));
      const res = await scanExpenseFile(up.id);
      setScan(res);
      const rowsIn = res.rows.map((r) => ({ ...r, amount: Number(r.amount).toFixed(2) }));
      // The statement names its account; a receipt usually doesn't, so start
      // from the card you used last time (still shown, easy to change).
      const guess = res.account_id || lastAccount(data.accounts);
      setAccountId(guess);
      setRows(guess && guess !== res.account_id ? (await recheckScan(guess, rowsIn).catch(() => ({ rows: rowsIn }))).rows : rowsIn);
      setStage("review");
    } catch (err) {
      setError(err.message || "Couldn't read that.");
      setStage("pick");
    }
  }

  async function changeAccount(e) {
    const id = e.target.value;
    setAccountId(id);
    try {
      const res = await recheckScan(id || null, rows);
      setRows(res.rows);
    } catch {
      /* keep the current flags */
    }
  }

  const update = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const chosen = rows.filter((r) => r.include && r.type !== "skip");
  const missingWho = chosen.some((r) => r.type === "money_in" && !r.partner);
  const total = chosen.filter((r) => r.type === "expense").reduce((s, r) => s + Number(r.amount || 0), 0);

  async function add() {
    setBusy(true);
    setError("");
    try {
      const res = await applyScan({
        file_id: scan.file_id,
        document_type: scan.document_type,
        account_id: accountId || null,
        rows,
      });
      try {
        if (accountId) localStorage.setItem(LAST_ACCOUNT, String(accountId));
      } catch {
        /* a convenience only */
      }
      onClose(true, res.added);
    } catch (err) {
      setError(err.message || "Couldn't add them.");
      setBusy(false);
    }
  }

  const footer =
    stage === "review" ? (
      <div className="flex flex-col gap-2">
        {missingWho && <p className="text-xs text-amber-700">Pick who put the money in for each deposit.</p>}
        <Button onClick={add} disabled={busy || !chosen.length || missingWho} className="w-full py-3">
          {busy ? "Adding…" : chosen.length ? `Add ${chosen.length} item${chosen.length === 1 ? "" : "s"}` : "Nothing ticked"}
        </Button>
      </div>
    ) : null;

  return (
    <Modal isOpen title="Scan a receipt or statement" onClose={() => onClose(false)} footer={footer} maxWidthClass="max-w-xl">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} />

      {stage === "pick" && (
        <div className="text-center py-6 space-y-3">
          <div className="text-4xl">🧾</div>
          <p className="text-sm text-birdie-charcoal/70">
            A photo of a receipt, a screenshot of an order, or a bank or card statement (PDF or screenshot).
          </p>
          <Button onClick={() => fileRef.current?.click()}>Choose a photo or file</Button>
          {!data.scan_enabled && (
            <p className="text-xs text-amber-700">
              Reading isn't switched on yet (OPENROUTER_API_KEY on the server), so this will only explain how.
            </p>
          )}
        </div>
      )}

      {stage === "reading" && (
        <div className="text-center py-12 space-y-2">
          <div className="text-4xl animate-pulse">🔎</div>
          <p className="text-sm font-semibold text-birdie-charcoal">Reading it…</p>
          <p className="text-xs text-birdie-charcoal/55">A receipt takes a few seconds; a long statement up to a minute.</p>
        </div>
      )}

      {stage === "review" && (
        <div className="space-y-3">
          <p className="text-sm text-birdie-charcoal/70">
            {scan.document_type === "receipt" ? "Here's what the receipt says." : `Found ${rows.length} lines.`} Check
            them, untick anything you don't want, then add.
          </p>
          <label className="block">
            <span className="block text-xs font-semibold text-birdie-charcoal/70 mb-1">
              Paid with / account{scan.account_hint ? ` (it says “${scan.account_hint}”)` : ""}
            </span>
            <select value={accountId} onChange={changeAccount} className={INPUT}>
              <option value="">Not sure yet</option>
              {data.accounts
                .filter((a) => !a.archived)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.owner !== "company" ? ` (${a.owner}'s)` : ""}
                  </option>
                ))}
            </select>
          </label>

          {rows.length === 0 ? (
            <p className="text-sm text-birdie-charcoal/60 py-4 text-center">Nothing readable in that one. Try a clearer photo.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li
                  key={r.key}
                  className={`rounded-xl border p-3 ${
                    r.include && r.type !== "skip" ? "border-birdie-green/25 bg-white" : "border-birdie-green/10 bg-birdie-cream/50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={Boolean(r.include) && r.type !== "skip"}
                      disabled={r.type === "skip"}
                      onChange={(e) => update(r.key, { include: e.target.checked })}
                      className="mt-1 h-4 w-4 min-h-0 min-w-0 accent-birdie-green"
                      aria-label={`Add ${r.description}`}
                    />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <input
                          value={r.description}
                          onChange={(e) => update(r.key, { description: e.target.value })}
                          className="min-w-0 flex-1 bg-transparent text-sm font-medium text-birdie-charcoal outline-none border-b border-transparent focus:border-birdie-green/30"
                        />
                        <input
                          value={r.amount}
                          onChange={(e) => update(r.key, { amount: e.target.value })}
                          inputMode="decimal"
                          className={`w-24 shrink-0 text-right bg-transparent text-sm font-semibold tabular-nums outline-none border-b border-transparent focus:border-birdie-green/30 ${
                            Number(r.amount) < 0 ? "text-birdie-green" : "text-birdie-charcoal"
                          }`}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span
                          className={`px-1.5 py-0.5 rounded-full font-semibold ${
                            r.type === "money_in"
                              ? "bg-blue-100 text-blue-800"
                              : r.type === "skip"
                                ? "bg-birdie-charcoal/10 text-birdie-charcoal/60"
                                : "bg-birdie-green/10 text-birdie-green"
                          }`}
                        >
                          {Number(r.amount) < 0 && r.type === "expense" ? "Refund" : TYPE_LABEL[r.type]}
                        </span>
                        <input
                          type="date"
                          value={r.date || ""}
                          onChange={(e) => update(r.key, { date: e.target.value })}
                          className="w-[8.5rem] bg-transparent text-birdie-charcoal/60 outline-none"
                        />
                        {r.type === "expense" && (
                          <select
                            value={r.category}
                            onChange={(e) => update(r.key, { category: e.target.value })}
                            className="min-w-0 flex-1 bg-transparent text-birdie-charcoal/60 outline-none"
                          >
                            {data.categories.map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        )}
                        {r.type === "money_in" && (
                          <select
                            value={r.partner || ""}
                            onChange={(e) => update(r.key, { partner: e.target.value || null })}
                            className={`bg-transparent outline-none ${r.partner ? "text-birdie-charcoal/70" : "text-amber-700 font-semibold"}`}
                          >
                            <option value="">From who?</option>
                            {data.partners.map((p) => (
                              <option key={p}>{p}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      {r.reason && <div className="text-[11px] text-birdie-charcoal/50">{r.reason}</div>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {total !== 0 && (
            <p className="text-right text-xs text-birdie-charcoal/55">
              Expenses ticked: <strong>${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
